import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, take } from 'rxjs/operators';

import { Injectable, computed, effect, inject } from '@angular/core';

import { RouteFacade } from '@mitsubishi/core';
import { CertificationItem, CurrencyUtils, Study } from '@mitsubishi/shared/data-access';
import { SpinnerService, StateSpinnerService } from '@mitsubishi/ui/spinner';
import { getStateActionErroredFactory } from '@mitsubishi/utils';

import { hasActionsExecuting } from '@ngxs-labs/actions-executing';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Actions, Store, ofActionCompleted } from '@ngxs/store';

import { EditProjectCompaniesRequest, Project, ProjectDetails, ProjectLoadOptions } from '../../models';
import { EnergyAnalisesStore } from '../../stores/energy-analysis/energy-analyses.store';
import { ProjectSelectionsSelectors } from '../selection/selections.selectors';
import { STUDY_READ_SOURCE } from '../study/study-read-source.token';
import { ProjectActions } from './project.actions';
import { ProjectSelectors } from './project.selectors';

@Injectable({
  providedIn: 'root',
})
export class ProjectFacade extends StateSpinnerService {
  #routeFacade = inject(RouteFacade);
  /** The two study-derived members below read whichever backing the flag selects (P1-CONSUMERS.md D3). */
  readonly #studySource = inject(STUDY_READ_SOURCE);
  private onActionErrored = getStateActionErroredFactory();

  activeProjectDetails$ = this.store.select(ProjectSelectors.projectDetails);
  activeProjectDetails = this.store.selectSignal(ProjectSelectors.projectDetails);
  activeProjectCompanies$ = this.store.select(ProjectSelectors.projectCompanies);
  projectLoading$ = this.store
    .select(hasActionsExecuting([ProjectActions.Load]))
    .pipe(debounceTime(150), distinctUntilChanged());
  activeProjectOrganizationId$ = this.store.select(ProjectSelectors.projectOrganizationId);
  activeProjectOrganizationId = this.store.selectSignal(ProjectSelectors.projectOrganizationId);

  canUpdateProjectCertificationOrUnitMeasures$ = this.#studySource.canUpdateProjectCertificationOrUnitMeasures$;

  projectLoadCompleted$ = this.actions$.pipe(ofActionCompleted(ProjectActions.Load));

  activeProjectId$ = this.store.select(ProjectSelectors.projectId);

  projectFeature = this.store.selectSignal(ProjectSelectors.projectFeature);
  projectCertification = this.store.selectSignal(ProjectSelectors.projectCertification);
  #studyCurrencySymbol = this.#studySource.activeStudyCurrencySymbol;
  #selectionCurrencySymbol = this.store.selectSignal(ProjectSelectionsSelectors.getActiveSelectionCurrencySymbol);
  #energyAnalysisStore = inject(EnergyAnalisesStore);
  #energyAnalysisCurrencySymbol = computed(() =>
    CurrencyUtils.getCurrencySymbolFromCode(this.#energyAnalysisStore.activeEnergyAnalysis()?.currencyCode)
  );

  // Working-currency symbol for the active module. Energy Analysis has no selection/study, so it
  // derives the symbol from the analysis-level currencyCode — the same shape study/selection use.
  currentSelectionOrStudyCurrencySymbol = computed(() => {
    if (this.#routeFacade.isEnergyAnalysisModuleSignal()) {
      return this.#energyAnalysisCurrencySymbol();
    }
    return this.#routeFacade.isStudyModuleSignal() ? this.#studyCurrencySymbol() : this.#selectionCurrencySymbol();
  });

  constructor(store: Store, actions$: Actions, spinnerService: SpinnerService) {
    super(store, spinnerService, actions$);

    this.addSpinnerActions([ProjectActions.Load]);
  }

  get currentProject(): Project {
    return this.store.selectSnapshot(ProjectSelectors.current);
  }

  get currentProjectDetails() {
    return this.store.selectSnapshot(ProjectSelectors.projectDetails);
  }

  get currentProjectId(): string {
    return this.store.selectSnapshot(ProjectSelectors.projectId);
  }

  getCurrentProjectCode(): string {
    return this.store.selectSnapshot(ProjectSelectors.projectCode);
  }

  get activeProjectDetailsSnapshot() {
    return this.store.selectSnapshot(ProjectSelectors.projectDetails);
  }

  get projectHasEuroventCertification(): boolean {
    return (
      Object.values(this.currentProjectDetails?.certification ?? {}).some(cert => cert.certificationId === 4) ?? false
    );
  }

  firstProjectId$(): Observable<string> {
    return this.activeProjectDetails$.pipe(
      filter(project => !!project),
      map(project => project?.uuid as string),
      take(1)
    );
  }

  @Dispatch()
  loadProject = (projectId: string, options?: ProjectLoadOptions) => new ProjectActions.Load(projectId, options);

  @Dispatch()
  updateProjectTitle(projectId: string, title: string) {
    return new ProjectActions.UpdateProjectTitle(projectId, title);
  }

  @Dispatch()
  addStudyToProject(study: Study) {
    return new ProjectActions.AddStudy(study);
  }

  @Dispatch()
  setProject(project: Project) {
    return new ProjectActions.Set(project);
  }

  /**
   * @deprecated The method is not used anymore
   */
  @Dispatch()
  updateProjectState(projectData: Partial<ProjectDetails>) {
    return new ProjectActions.UpdateState(projectData);
  }

  /**
   * @deprecated The method is not used anymore
   */
  @Dispatch()
  updateProjectName(title: string) {
    return new ProjectActions.UpdateState({ title });
  }

  @Dispatch()
  activateProject(projectId: string) {
    return new ProjectActions.Activate(projectId);
  }

  @Dispatch()
  archiveProject(projectId: string) {
    return new ProjectActions.Archive(projectId);
  }

  @Dispatch()
  restoreProject(projectId: string) {
    return new ProjectActions.Restore(projectId);
  }

  @Dispatch()
  addFavoriteProject(projectId: string) {
    return new ProjectActions.AddFavorite(projectId);
  }

  @Dispatch()
  removeFavoriteProject(projectId: string) {
    return new ProjectActions.RemoveFavorite(projectId);
  }

  // Non e' @Dispatch() come le altre: chi duplica deve poter reagire al fallimento HTTP per
  // chiudere la trace websocket, quindi il metodo torna lo stream dell'azione andata in errore.
  duplicateProject = (projectId: string, requestTraceId?: string) => {
    this.store.dispatch(new ProjectActions.Duplicate(projectId, requestTraceId));
    return this.onActionErrored(ProjectActions.Duplicate);
  };

  @Dispatch()
  deleteProject(projectId: string) {
    return new ProjectActions.Delete(projectId);
  }

  @Dispatch()
  handleProjectDeleted() {
    return new ProjectActions.DeleteSuccess();
  }

  @Dispatch()
  resetProject() {
    return new ProjectActions.Reset();
  }

  @Dispatch()
  editProjectCompanies(projectId: string, request: EditProjectCompaniesRequest) {
    return new ProjectActions.EditProjectCompanies(projectId, request);
  }

  @Dispatch()
  updateCertification(projectId: string, certification: CertificationItem[]) {
    return new ProjectActions.UpdateCertification(projectId, certification);
  }

  @Dispatch()
  updateUnitsOfMisures(projectId: string, uom: any) {
    return new ProjectActions.UpdateUnitsOfMisures(projectId, uom);
  }
}
